<template>
  <div class="tool-bar-container">
    <div
      ref="toolFloat"
      class="tool-bar"
      :class="toolBarDockClass"
      :style="toolBarStyle"
      @mousedown.prevent="onToolDragStart"
      @touchstart.prevent="onToolDragStart"
      @click.capture="onToolClickCapture"
    >
      <div class="tool-item" @click="openMemo">
        <i class="el-icon-edit"></i>
        <span>备忘录</span>
      </div>
      <Memorandum ref="memorandum" @update:count="memoCount = $event" />
      <help-center>
        <div class="tool-item">
            <i class="el-icon-question" style="color: #f17458;"></i>
            <span>帮助</span>
        </div>
      </help-center>
    </div>
  </div>
</template>
<script>
import HelpCenter from '@/views/helpCenter'
import Memorandum from '@/components/Memorandum'
export default {
  components: {
    HelpCenter,
    Memorandum,
  },

  data() {
    return {
      memoCount: 0,
      toolFloatX: 0,
      toolFloatY: 0,
      isToolDragging: false,
      toolDragStartClientX: 0,
      toolDragStartClientY: 0,
      toolDragStartX: 0,
      toolDragStartY: 0,
      toolSuppressNextClick: false,
      toolHasMovedDuringDrag: false,
      toolDockedRight: true,
      toolViewportWidth: 0,
      toolViewportHeight: 0,
      toolViewportObserver: null,
      toolViewportRafId: 0,
    }
  },

  computed: {
    toolBarStyle() {
      return {
        left: `${this.toolFloatX}px`,
        top: `${this.toolFloatY}px`
      }
    },
    toolBarDockClass() {
      return {
        'is-docked-right': this.toolDockedRight,
        'is-docked-left': !this.toolDockedRight,
      }
    },
  },

  mounted() {
    this.initToolFloatPosition()
    this.updateViewportCache()
    this.startViewportObserver()
    window.addEventListener('resize', this.onWindowResize, { passive: true })
  },

  beforeDestroy() {
    this.stopViewportObserver()
    if (this.toolViewportRafId) cancelAnimationFrame(this.toolViewportRafId)
    this.removeToolDragListeners()
    window.removeEventListener('resize', this.onWindowResize)
  },

  methods: {
    openMemo() {
      this.$refs.memorandum.showPanel()
    },
    initToolFloatPosition() {
      this.$nextTick(() => {
        const rect = this.getToolFloatRect()
        const { maxX, maxY, viewportHeight } = this.getToolMaxPosition(rect)
        this.toolFloatX = maxX
        this.toolFloatY = this.clamp(viewportHeight - rect.height - 50, 0, maxY)
        this.toolDockedRight = true
      })
    },
    onToolClickCapture(event) {
      if (!this.toolSuppressNextClick) return
      this.toolSuppressNextClick = false
      event.preventDefault()
      event.stopPropagation()
    },
    onToolDragStart(event) {
      const point = this.getClientPoint(event)
      if (!point) return

      this.isToolDragging = true
      this.toolHasMovedDuringDrag = false
      this.toolDragStartClientX = point.clientX
      this.toolDragStartClientY = point.clientY
      this.toolDragStartX = this.toolFloatX
      this.toolDragStartY = this.toolFloatY

      this.addToolDragListeners()
    },
    onToolDragMove(event) {
      if (!this.isToolDragging) return
      const point = this.getClientPoint(event)
      if (!point) return
      if (event && event.cancelable) event.preventDefault()

      const deltaX = point.clientX - this.toolDragStartClientX
      const deltaY = point.clientY - this.toolDragStartClientY
      if (!this.toolHasMovedDuringDrag && (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
        this.toolHasMovedDuringDrag = true
        this.toolSuppressNextClick = true
      }

      const rect = this.getToolFloatRect()
      const { maxX, maxY } = this.getToolMaxPosition(rect)

      this.toolFloatX = this.clamp(this.toolDragStartX + deltaX, 0, maxX)
      this.toolFloatY = this.clamp(this.toolDragStartY + deltaY, 0, maxY)
    },
    onToolDragEnd() {
      if (!this.isToolDragging) return
      this.isToolDragging = false
      this.removeToolDragListeners()

      if (!this.toolHasMovedDuringDrag) return

      const rect = this.getToolFloatRect()
      const { maxX, maxY, viewportWidth } = this.getToolMaxPosition(rect)
      const shouldDockRight = this.toolFloatX + rect.width / 2 >= viewportWidth / 2

      this.toolDockedRight = shouldDockRight
      this.toolFloatX = shouldDockRight ? maxX : 0
      this.toolFloatY = this.clamp(this.toolFloatY, 0, maxY)
    },
    onWindowResize() {
      const rect = this.getToolFloatRect()
      const { maxX, maxY } = this.getToolMaxPosition(rect)
      this.toolFloatX = this.toolDockedRight ? maxX : 0
      this.toolFloatY = this.clamp(this.toolFloatY, 0, maxY)
    },
    getViewportSize() {
      const root = document.documentElement
      const width = (root && root.clientWidth) || window.innerWidth || 0
      const height = (root && root.clientHeight) || window.innerHeight || 0
      return { width, height }
    },
    updateViewportCache() {
      const { width, height } = this.getViewportSize()
      this.toolViewportWidth = width
      this.toolViewportHeight = height
    },
    onViewportResize() {
      const { width, height } = this.getViewportSize()
      if (width === this.toolViewportWidth && height === this.toolViewportHeight) return
      this.toolViewportWidth = width
      this.toolViewportHeight = height
      if (this.toolViewportRafId) cancelAnimationFrame(this.toolViewportRafId)
      this.toolViewportRafId = requestAnimationFrame(() => {
        this.toolViewportRafId = 0
        this.onWindowResize()
      })
    },
    startViewportObserver() {
      const root = document.documentElement
      if (root && typeof ResizeObserver !== 'undefined' && !this.toolViewportObserver) {
        this.toolViewportObserver = new ResizeObserver(() => {
          this.onViewportResize()
        })
        this.toolViewportObserver.observe(root)
      }
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportResize, { passive: true })
      }
    },
    stopViewportObserver() {
      if (this.toolViewportObserver) {
        this.toolViewportObserver.disconnect()
        this.toolViewportObserver = null
      }
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onViewportResize)
      }
    },
    getToolMaxPosition(rect) {
      const { width: viewportWidth, height: viewportHeight } = this.getViewportSize()
      const rightMargin = 0
      const bottomMargin = 16
      const maxX = Math.max(0, viewportWidth - rect.width - rightMargin)
      const maxY = Math.max(0, viewportHeight - rect.height - bottomMargin)
      return { maxX, maxY, viewportWidth, viewportHeight }
    },
    getToolFloatRect() {
      const el = this.$refs.toolFloat
      if (!el || !el.getBoundingClientRect) return { width: 50, height: 140 }
      return el.getBoundingClientRect()
    },
    getClientPoint(event) {
      if (!event) return null
      if (event.touches && event.touches[0]) return event.touches[0]
      if (typeof event.clientX === 'number' && typeof event.clientY === 'number') return event
      return null
    },
    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    addToolDragListeners() {
      window.addEventListener('mousemove', this.onToolDragMove)
      window.addEventListener('mouseup', this.onToolDragEnd)
      window.addEventListener('touchmove', this.onToolDragMove, { passive: false })
      window.addEventListener('touchend', this.onToolDragEnd)
      window.addEventListener('touchcancel', this.onToolDragEnd)
    },
    removeToolDragListeners() {
      window.removeEventListener('mousemove', this.onToolDragMove)
      window.removeEventListener('mouseup', this.onToolDragEnd)
      window.removeEventListener('touchmove', this.onToolDragMove)
      window.removeEventListener('touchend', this.onToolDragEnd)
      window.removeEventListener('touchcancel', this.onToolDragEnd)
    },
  }
}
</script>
<style lang="scss" scoped>
.tool-bar-container {
  pointer-events: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
}
.tool-bar {
  pointer-events: auto;
  position: fixed;
  top: 0;
  left: 0;
  width: 50px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
  cursor: grab;
  user-select: none;
  touch-action: none;
  will-change: left, top;

  &.is-docked-right {
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.is-docked-left {
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:active {
    cursor: grabbing;
  }

  .tool-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    cursor: pointer;
    .tool-item-gif {
      width: 18px;
      height: 18px;
      object-fit: contain;
      display: block;
    }
    i {
      font-size: 18px;
    }
    span {
      font-size: 12px;
    }
  }
}
</style>

<style lang="scss">
.tool-memo-dialog {
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0;
    height: 70vh;
  }
  background: transparent;
  box-shadow: none;
}
</style>
